/**
 * Stego Panel — Encode/Decode UI component.
 *
 * Provides a tabbed interface for encoding payloads into MP3s
 * and decoding payloads from MP3s.
 */

import { encodePayload } from "../stego/encoder.js";
import { decodeCarrierAll, decodeImage } from "../stego/decoder.js";
import { wrapFileEnvelope, unwrapFileEnvelope } from "../stego/file-envelope.js";
import { Technique, techniqueName } from "../stego/header.js";
import { createPolyglot } from "../stego/techniques/polyglot.js";
import { embedInCoverArt } from "../stego/techniques/cover-art.js";
import { toBase64 } from "../crypto/utils.js";
import type { ApiClient } from "../services/api-client.js";

interface CompletedTask {
  id: string;
  prompt: string;
}

interface StegoPanelOptions {
  onImportLibraryTrack?: (clipId: string) => Promise<string>;
}

type ProgressCallback = (stage: string) => void;

let currentTab: "encode" | "decode" = "encode";
let apiClient: ApiClient | null = null;
let completedTasks: CompletedTask[] = [];
let importLibraryTrack: ((clipId: string) => Promise<string>) | null = null;

export function initStegoPanel(
  api: ApiClient,
  tasks: CompletedTask[],
  options?: StegoPanelOptions
): void {
  apiClient = api;
  completedTasks = tasks;
  importLibraryTrack = options?.onImportLibraryTrack ?? null;
  render();
  attachHandlers();
}

export function updateCompletedTasks(tasks: CompletedTask[]): void {
  completedTasks = tasks;
  const select = document.getElementById(
    "stego-task-select"
  ) as HTMLSelectElement | null;
  if (select && currentTab === "encode") {
    renderTaskOptions(select);
  }
}

function render(): void {
  const container = document.getElementById("stego-container");
  if (!container) return;

  container.innerHTML = `
    <div class="stego-tabs">
      <button class="stego-tab ${currentTab === "encode" ? "active" : ""}" data-tab="encode">Encode</button>
      <button class="stego-tab ${currentTab === "decode" ? "active" : ""}" data-tab="decode">Decode</button>
    </div>
    <div class="stego-content">
      ${currentTab === "encode" ? renderEncodeTab() : renderDecodeTab()}
    </div>
  `;

  attachHandlers();
}

function renderEncodeTab(): string {
  return `
    <div class="stego-encode">
      <div class="stego-mode-toggle">
        <label class="stego-radio">
          <input type="radio" name="encode-mode" value="generated" checked />
          <span>Encode Generated Track</span>
        </label>
        <label class="stego-radio">
          <input type="radio" name="encode-mode" value="local" />
          <span>Encode Local MP3</span>
        </label>
      </div>

      <div id="encode-generated-section">
        <div class="form-group">
          <label class="form-label" for="stego-task-select">Select Track</label>
          <select class="form-input" id="stego-task-select">
            <option value="">-- Select a completed track --</option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Techniques & Payloads</label>
          <div class="stego-technique-checkboxes">
            <label class="stego-checkbox">
              <input type="checkbox" name="stego-technique" value="metadata" checked />
              <span>Metadata (ID3 PRIV)</span>
            </label>
            <div class="stego-technique-payload" id="payload-metadata">
              <textarea class="form-textarea" id="payload-metadata-text" placeholder="Text to hide in ID3 metadata..." style="min-height: 60px;"></textarea>
            </div>

            <label class="stego-checkbox">
              <input type="checkbox" name="stego-technique" value="polyglot" />
              <span>Polyglot (MP3/ZIP)</span>
            </label>
            <div class="stego-technique-payload hidden" id="payload-polyglot">
              <textarea class="form-textarea" id="payload-polyglot-text" placeholder="Text to hide in polyglot..." style="min-height: 60px;"></textarea>
              <div class="stego-technique-file-section">
                <span class="text-muted" style="font-size: var(--text-xs);">Or attach a file instead:</span>
                <div class="stego-drop-zone stego-drop-zone-sm" id="payload-polyglot-drop-zone">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <p>Drop file here or click to browse</p>
                  <input type="file" id="payload-polyglot-file-input" class="hidden" />
                </div>
                <div class="stego-file-name hidden" id="payload-polyglot-file-name"></div>
              </div>
            </div>

            <label class="stego-checkbox">
              <input type="checkbox" name="stego-technique" value="cover_art" />
              <span>Cover Art (Image LSB)</span>
            </label>
            <div class="stego-technique-payload hidden" id="payload-cover_art">
              <textarea class="form-textarea" id="payload-cover_art-text" placeholder="Text to hide in cover art..." style="min-height: 60px;"></textarea>
            </div>
          </div>
        </div>
      </div>

      <div id="encode-local-section" class="hidden">
        <div class="stego-drop-zone" id="encode-drop-zone">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          <p>Drop MP3 file here or click to browse</p>
          <input type="file" accept=".mp3,audio/mpeg" id="encode-file-input" class="hidden" />
        </div>
        <div class="stego-file-name hidden" id="encode-file-name"></div>
        <p class="text-muted" style="font-size: var(--text-xs); margin-top: var(--space-2);">
          Local mode uses polyglot technique only (100% client-side).
        </p>
        <div class="form-group" style="margin-top: var(--space-3);">
          <label class="form-label" for="local-payload-text">Text Payload</label>
          <textarea class="form-textarea" id="local-payload-text" placeholder="Text to hide..." style="min-height: 60px;"></textarea>
        </div>
        <div class="form-group">
          <label class="form-label">File Payload <span class="text-muted" style="font-weight: normal;">(overrides text)</span></label>
          <div class="stego-drop-zone stego-drop-zone-sm" id="local-payload-drop-zone">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            <p>Drop file here or click to browse</p>
            <input type="file" id="local-payload-file-input" class="hidden" />
          </div>
          <div class="stego-file-name hidden" id="local-payload-file-name"></div>
        </div>
      </div>

      <div class="form-group">
        <label class="form-label" for="stego-passphrase">Passphrase</label>
        <input type="password" class="form-input" id="stego-passphrase" placeholder="Encryption passphrase" autocomplete="off" />
      </div>

      <div class="stego-progress hidden" id="encode-progress">
        <div class="spinner spinner-sm"></div>
        <span id="encode-progress-text">Preparing...</span>
      </div>

      <button class="btn btn-primary" id="encode-btn" style="margin-top: var(--space-4);">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        Encode
      </button>
    </div>
  `;
}

function renderDecodeTab(): string {
  return `
    <div class="stego-decode">
      <div class="stego-drop-zone" id="decode-drop-zone">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        <p>Drop encoded MP3 or image here</p>
        <input type="file" accept=".mp3,.png,.jpg,.jpeg,audio/mpeg,image/png,image/jpeg" id="decode-file-input" class="hidden" />
      </div>
      <div class="stego-file-name hidden" id="decode-file-name"></div>

      <div class="form-group" style="margin-top: var(--space-4);">
        <label class="form-label" for="decode-passphrase">Passphrase</label>
        <input type="password" class="form-input" id="decode-passphrase" placeholder="Decryption passphrase" autocomplete="off" />
      </div>

      <div class="stego-progress hidden" id="decode-progress">
        <div class="spinner spinner-sm"></div>
        <span id="decode-progress-text">Preparing...</span>
      </div>

      <button class="btn btn-primary" id="decode-btn" style="margin-top: var(--space-4);">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 5-5 5 5 0 0 1 5 5v4"/>
          <line x1="12" y1="16" x2="12" y2="19"/>
        </svg>
        Decode
      </button>

      <div class="hidden" id="decode-results-container"></div>
    </div>
  `;
}

function attachHandlers(): void {
  // Tab switching
  document.querySelectorAll(".stego-tab").forEach((tab) => {
    tab.addEventListener("click", () => {
      currentTab = (tab as HTMLElement).dataset["tab"] as "encode" | "decode";
      render();
    });
  });

  if (currentTab === "encode") {
    attachEncodeHandlers();
  } else {
    attachDecodeHandlers();
  }
}

// ---- Encode ----

let encodeLocalFile: Uint8Array | null = null;
let polyglotPayloadFile: Uint8Array | null = null;
let polyglotPayloadFileName: string | null = null;
let localPayloadFile: Uint8Array | null = null;
let localPayloadFileName: string | null = null;

function attachEncodeHandlers(): void {
  // Populate task select
  const select = document.getElementById(
    "stego-task-select"
  ) as HTMLSelectElement;
  if (select) renderTaskOptions(select);

  // Carrier mode toggle (generated vs local)
  document.querySelectorAll('input[name="encode-mode"]').forEach((radio) => {
    radio.addEventListener("change", (e) => {
      const mode = (e.target as HTMLInputElement).value;
      const genSection = document.getElementById("encode-generated-section");
      const localSection = document.getElementById("encode-local-section");
      genSection?.classList.toggle("hidden", mode === "local");
      localSection?.classList.toggle("hidden", mode === "generated");
    });
  });

  // Technique checkboxes toggle per-technique payload sections
  document.querySelectorAll<HTMLInputElement>('input[name="stego-technique"]').forEach((cb) => {
    cb.addEventListener("change", () => {
      const section = document.getElementById(`payload-${cb.value}`);
      section?.classList.toggle("hidden", !cb.checked);
    });
  });

  // Carrier file drop zone (local mode)
  setupDropZone(
    "encode-drop-zone",
    "encode-file-input",
    "encode-file-name",
    (data) => { encodeLocalFile = data; }
  );

  // Polyglot technique file drop zone
  setupDropZone(
    "payload-polyglot-drop-zone",
    "payload-polyglot-file-input",
    "payload-polyglot-file-name",
    (data, name) => { polyglotPayloadFile = data; polyglotPayloadFileName = name ?? null; }
  );

  // Local mode file payload drop zone
  setupDropZone(
    "local-payload-drop-zone",
    "local-payload-file-input",
    "local-payload-file-name",
    (data, name) => { localPayloadFile = data; localPayloadFileName = name ?? null; }
  );

  // Encode button
  document.getElementById("encode-btn")?.addEventListener("click", handleEncode);
}

function renderTaskOptions(select: HTMLSelectElement): void {
  const current = select.value;
  select.innerHTML = '<option value="">-- Select a completed track --</option>';
  for (const task of completedTasks) {
    const opt = document.createElement("option");
    opt.value = task.id;
    opt.textContent = `${task.prompt.slice(0, 50)}${task.prompt.length > 50 ? "..." : ""} (${task.id.slice(0, 8)})`;
    select.appendChild(opt);
  }
  select.value = current;
}

async function handleEncode(): Promise<void> {
  const passphrase = (
    document.getElementById("stego-passphrase") as HTMLInputElement
  )?.value;
  if (!passphrase) { showStegoError("Please enter a passphrase."); return; }

  const isLocal = (
    document.querySelector('input[name="encode-mode"]:checked') as HTMLInputElement
  )?.value === "local";
  const btn = document.getElementById("encode-btn") as HTMLButtonElement;
  const progress = document.getElementById("encode-progress")!;
  const progressText = document.getElementById("encode-progress-text")!;

  btn.disabled = true;
  progress.classList.remove("hidden");

  const updateProgress: ProgressCallback = (stage) => {
    progressText.textContent = stage;
  };

  try {

    if (isLocal) {
      if (!encodeLocalFile) { showStegoError("Please select an MP3 file."); return; }

      // Local mode is always polyglot — prefer file payload, fall back to text
      let localPayload: Uint8Array;
      let localIsFile: boolean;
      if (localPayloadFile) {
        localPayload = localPayloadFileName
          ? wrapFileEnvelope(localPayloadFileName, localPayloadFile)
          : localPayloadFile;
        localIsFile = true;
      } else {
        const text = (document.getElementById("local-payload-text") as HTMLTextAreaElement)?.value;
        if (!text) { showStegoError("Please enter a text payload or select a file."); return; }
        localPayload = new TextEncoder().encode(text);
        localIsFile = false;
      }

      updateProgress("Deriving key (this may take a few seconds)...");
      const { blob } = await encodePayload(localPayload, passphrase, {
        technique: Technique.POLYGLOT,
        isFile: localIsFile,
      });

      updateProgress("Creating polyglot file...");
      const result = await createPolyglot(encodeLocalFile, blob);

      updateProgress("Done!");
      downloadBlob(result, "encoded.mp3", "audio/mpeg");
    } else {
      // Server-side embed (generated track)
      let taskId = (
        document.getElementById("stego-task-select") as HTMLSelectElement
      )?.value;
      if (!taskId) { showStegoError("Please select a track."); return; }

      // Auto-import library tracks before encoding
      if (taskId.startsWith("library:") && importLibraryTrack) {
        const clipId = taskId.slice("library:".length);
        updateProgress("Importing library track...");
        taskId = await importLibraryTrack(clipId);
      }

      // Read selected techniques from checkboxes
      const checked = document.querySelectorAll<HTMLInputElement>(
        'input[name="stego-technique"]:checked'
      );
      const techniques = Array.from(checked).map((el) => el.value as "metadata" | "polyglot" | "cover_art");

      if (techniques.length === 0) {
        showStegoError("Please select at least one technique.");
        return;
      }

      if (!apiClient) throw new Error("Not connected");

      // Process each technique sequentially with its own payload
      for (let i = 0; i < techniques.length; i++) {
        const technique = techniques[i]!;
        const techniqueId = technique === "metadata" ? Technique.METADATA
          : technique === "cover_art" ? Technique.COVER_ART
          : Technique.POLYGLOT;
        const stepLabel = techniques.length > 1 ? ` (${i + 1}/${techniques.length})` : "";

        // Resolve per-technique payload
        let currentPayload: Uint8Array;
        let isFile = false;

        if (technique === "polyglot" && polyglotPayloadFile) {
          currentPayload = polyglotPayloadFileName
            ? wrapFileEnvelope(polyglotPayloadFileName, polyglotPayloadFile)
            : polyglotPayloadFile;
          isFile = true;
        } else {
          const textEl = document.getElementById(`payload-${technique}-text`) as HTMLTextAreaElement | null;
          const text = textEl?.value;
          if (!text) {
            showStegoError(`Please enter a payload for ${techniqueName(techniqueId)}.`);
            return;
          }
          currentPayload = new TextEncoder().encode(text);
        }

        updateProgress(`Deriving key${stepLabel}...`);
        const { blob } = await encodePayload(currentPayload, passphrase, {
          technique: techniqueId,
          isFile,
        });

        if (technique === "cover_art") {
          updateProgress(`Downloading cover art${stepLabel}...`);
          const coverUrl = await apiClient.coverUrl(taskId);
          if (!coverUrl) throw new Error("No cover art found for this track.");

          const coverResp = await fetch(coverUrl);
          const coverData = new Uint8Array(await coverResp.arrayBuffer());

          updateProgress(`Embedding payload in cover art${stepLabel}...`);
          const encodedPng = await embedInCoverArt(coverData, blob);

          updateProgress(`Injecting cover art into MP3${stepLabel}...`);
          const pngB64 = toBase64(encodedPng);
          await apiClient.stegoEmbed({
            task_id: taskId,
            technique: "cover_art",
            blob_b64: pngB64,
          });
        } else {
          updateProgress(`Embedding ${technique}${stepLabel}...`);
          const blobB64 = toBase64(blob);
          await apiClient.stegoEmbed({
            task_id: taskId,
            technique,
            blob_b64: blobB64,
          });
        }
      }

      updateProgress("Downloading encoded MP3...");
      const mp3Blob = await apiClient.stegoDownload(taskId);
      downloadBlob(
        new Uint8Array(await mp3Blob.arrayBuffer()),
        `suno-${taskId.slice(0, 8)}-stego.mp3`,
        "audio/mpeg"
      );

      updateProgress("Done!");
    }
  } catch (err) {
    showStegoError(err instanceof Error ? err.message : "Encoding failed.");
  } finally {
    btn.disabled = false;
    setTimeout(() => progress.classList.add("hidden"), 2000);
  }
}

// ---- Decode ----

let decodeFile: Uint8Array | null = null;

function attachDecodeHandlers(): void {
  setupDropZone("decode-drop-zone", "decode-file-input", "decode-file-name", (data) => {
    decodeFile = data;
  });

  document.getElementById("decode-btn")?.addEventListener("click", handleDecode);
}

async function handleDecode(): Promise<void> {
  if (!decodeFile) { showStegoError("Please select a file."); return; }

  const passphrase = (document.getElementById("decode-passphrase") as HTMLInputElement)?.value;
  if (!passphrase) { showStegoError("Please enter a passphrase."); return; }

  const btn = document.getElementById("decode-btn") as HTMLButtonElement;
  const progress = document.getElementById("decode-progress")!;
  const progressText = document.getElementById("decode-progress-text")!;

  btn.disabled = true;
  progress.classList.remove("hidden");

  try {
    progressText.textContent = "Detecting file type...";
    const isImage = isImageFile(decodeFile);

    progressText.textContent = "Deriving key (this may take a few seconds)...";

    let results: import("../stego/decoder.js").DecodeResult[];

    if (isImage) {
      // Image files only have one technique
      const result = await decodeImage(decodeFile, passphrase);
      results = [result];
    } else {
      // MP3 files — try all techniques
      results = await decodeCarrierAll(decodeFile, passphrase);
    }

    if (results.length === 0) {
      showStegoError("No steganographic payload found in this file.");
      return;
    }

    progressText.textContent = `Found ${results.length} payload${results.length > 1 ? "s" : ""}!`;

    // Render results
    const container = document.getElementById("decode-results-container")!;
    container.innerHTML = "";
    container.classList.remove("hidden");

    for (const result of results) {
      const resultDiv = document.createElement("div");
      resultDiv.className = "stego-result";

      const headerDiv = document.createElement("div");
      headerDiv.className = "stego-result-header";

      const badge = document.createElement("span");
      badge.className = "stego-technique-badge";
      badge.textContent = techniqueName(result.technique);
      headerDiv.appendChild(badge);

      const sizeSpan = document.createElement("span");
      sizeSpan.className = "stego-result-size";
      sizeSpan.textContent = formatSize(result.plaintext.length);
      headerDiv.appendChild(sizeSpan);

      resultDiv.appendChild(headerDiv);

      const textPre = document.createElement("pre");
      textPre.className = "stego-result-text";

      if (result.header.isFile) {
        const envelope = unwrapFileEnvelope(result.plaintext);
        const fileContent = envelope ? envelope.content : result.plaintext;
        const fileName = envelope?.filename ?? "decoded_payload.bin";
        textPre.textContent = envelope
          ? `[File: ${envelope.filename}, ${formatSize(fileContent.length)}]`
          : `[Binary file, ${formatSize(fileContent.length)}]`;
        resultDiv.appendChild(textPre);

        const dlBtn = document.createElement("button");
        dlBtn.className = "btn btn-secondary";
        dlBtn.style.marginTop = "var(--space-3)";
        dlBtn.textContent = "Download File";
        dlBtn.onclick = () => downloadBlob(fileContent, fileName, "application/octet-stream");
        resultDiv.appendChild(dlBtn);
      } else {
        const text = new TextDecoder().decode(result.plaintext);
        textPre.textContent = text;
        resultDiv.appendChild(textPre);
      }

      container.appendChild(resultDiv);
    }
  } catch (err) {
    showStegoError(err instanceof Error ? err.message : "Decoding failed.");
  } finally {
    btn.disabled = false;
    setTimeout(() => progress.classList.add("hidden"), 2000);
  }
}

// ---- Helpers ----

function setupDropZone(
  zoneId: string,
  inputId: string,
  nameId: string,
  onLoad: (data: Uint8Array, name?: string) => void
): void {
  const zone = document.getElementById(zoneId);
  const input = document.getElementById(inputId) as HTMLInputElement;
  const nameEl = document.getElementById(nameId);

  if (!zone || !input || !nameEl) return;

  zone.addEventListener("click", () => input.click());

  zone.addEventListener("dragover", (e) => {
    e.preventDefault();
    zone.classList.add("drag-over");
  });

  zone.addEventListener("dragleave", () => {
    zone.classList.remove("drag-over");
  });

  zone.addEventListener("drop", (e) => {
    e.preventDefault();
    zone.classList.remove("drag-over");
    const file = e.dataTransfer?.files[0];
    if (file) loadFile(file, nameEl, onLoad);
  });

  input.addEventListener("change", () => {
    const file = input.files?.[0];
    if (file) loadFile(file, nameEl, onLoad);
  });
}

function loadFile(
  file: File,
  nameEl: HTMLElement,
  onLoad: (data: Uint8Array, name?: string) => void
): void {
  nameEl.textContent = `${file.name} (${formatSize(file.size)})`;
  nameEl.classList.remove("hidden");

  const reader = new FileReader();
  reader.onload = () => {
    onLoad(new Uint8Array(reader.result as ArrayBuffer), file.name);
  };
  reader.readAsArrayBuffer(file);
}

function downloadBlob(data: Uint8Array, filename: string, mime: string): void {
  const blob = new Blob([data as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Detect image files by magic bytes (PNG: 89 50 4E 47, JPEG: FF D8). */
function isImageFile(data: Uint8Array): boolean {
  if (data.length < 4) return false;
  // PNG magic
  if (data[0] === 0x89 && data[1] === 0x50 && data[2] === 0x4e && data[3] === 0x47) return true;
  // JPEG magic
  if (data[0] === 0xff && data[1] === 0xd8) return true;
  return false;
}

function showStegoError(message: string): void {
  // Use the global error display
  const el = document.getElementById("error-text");
  const container = document.getElementById("error-message");
  if (el && container) {
    el.textContent = message;
    container.classList.remove("hidden");
    setTimeout(() => container.classList.add("hidden"), 6000);
  }
}
